import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, ListChecks } from 'lucide-react';
import { Tabs } from '@/components/ui/tabs';
import { tasksService } from '@/services/tasks.service';
import { additionalTasksService } from '@/services/additional-tasks.service';
import { SideDrawer } from './SideDrawer';
import { TaskRow, AdditionalTaskRow } from './TaskRow';
import { TaskDetailDrawer } from './TaskDetailDrawer';
import { fullName, initials } from './taskConstants';

interface EmployeeTasksDrawerProps {
  employee: { user_id: string; first_name: string | null; last_name: string | null } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EmployeeTasksDrawer({ employee, open, onOpenChange }: EmployeeTasksDrawerProps) {
  const [tab, setTab] = useState<'tasks' | 'additional'>('tasks');
  const [detailId, setDetailId] = useState<string | null>(null);

  const tasksQ = useQuery({
    queryKey: ['tasks', 'employee', employee?.user_id],
    queryFn: () => tasksService.tasksForEmployee(employee!.user_id),
    enabled: open && !!employee,
  });
  const additionalQ = useQuery({
    queryKey: ['additional-tasks', 'employee', employee?.user_id],
    queryFn: () => additionalTasksService.list({ user_id: employee!.user_id, limit: 100 }),
    enabled: open && !!employee && tab === 'additional',
  });

  const taskCount = tasksQ.data?.length ?? 0;
  const addCount = additionalQ.data?.pagination.total ?? 0;

  return (
    <>
      <SideDrawer open={open} onOpenChange={onOpenChange} title={fullName(employee?.first_name, employee?.last_name)}>
        {employee && (
          <div>
            <div className="flex items-center gap-3 border-b border-border/60 p-4 pr-12 sm:p-5 sm:pr-14">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold">
                {initials(employee.first_name, employee.last_name)}
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-base font-bold sm:text-lg">{fullName(employee.first_name, employee.last_name)}</h2>
                <p className="text-xs text-muted-foreground">{taskCount} tasks · {addCount} additional</p>
              </div>
            </div>

            <div className="p-4 sm:p-5">
              <Tabs
                className="mb-3"
                active={tab}
                onChange={(k) => setTab(k as 'tasks' | 'additional')}
                tabs={[
                  { key: 'tasks', label: <span className="flex items-center gap-1.5"><ListChecks className="h-4 w-4" />Tasks {taskCount}</span> },
                  { key: 'additional', label: <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4" />Additional {addCount}</span> },
                ]}
              />

              {tab === 'tasks' && (
                <div className="overflow-hidden rounded-lg border border-border/60">
                  {tasksQ.isLoading && <div className="space-y-px p-1">{[...Array(4)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-muted/50" />)}</div>}
                  {tasksQ.isError && <p className="p-6 text-center text-sm text-muted-foreground">Couldn’t load tasks.</p>}
                  {tasksQ.data && tasksQ.data.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No tasks assigned.</p>}
                  {tasksQ.data?.map((t) => <TaskRow key={t.id} task={t} onClick={() => setDetailId(t.id)} />)}
                </div>
              )}

              {tab === 'additional' && (
                <div className="overflow-hidden rounded-lg border border-border/60">
                  {additionalQ.isLoading && <div className="space-y-px p-1">{[...Array(3)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-muted/50" />)}</div>}
                  {additionalQ.isError && <p className="p-6 text-center text-sm text-muted-foreground">Couldn’t load additional work.</p>}
                  {additionalQ.data && additionalQ.data.data.length === 0 && (
                    <p className="p-6 text-center text-sm text-muted-foreground">No additional tasks created by this user.</p>
                  )}
                  {additionalQ.data?.data.map((a) => <AdditionalTaskRow key={a.id} item={a} />)}
                </div>
              )}
            </div>
          </div>
        )}
      </SideDrawer>

      <TaskDetailDrawer taskId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </>
  );
}
